import { ComponentPropsWithoutRef, useEffect, useState } from "react";
import { CoreFileItem, FileRadioIcon, FileRadioIconProps } from "./FileCard";
import styles from "./FilePreview.module.css";
import { checkIfImage } from "./fileHelper";
import { Box, BoxProps, TypographyProps } from "@mui/material";
import { useImagePreview } from "../../hooks";
import i18nPackage from "../../i18n/i18n";
import { OverflowText } from "../tooltip";

type FileFallbackProps = Pick<CoreFilePreviewProps, "slotProps"> & {
	fileName: string;
	children?: React.ReactNode;
};

function CoreFileFallback({ fileName, slotProps, children }: FileFallbackProps) {
	const showFileName = !slotProps?.fileName?.hide;
	return (
		<div
			{...slotProps?.fallbackContainer}
			className={`${styles.fallbackContainer} ${slotProps?.fallbackContainer?.className || ""}`}
		>
			<FileRadioIcon {...slotProps?.fallbackIcon} />
			{showFileName && (
				<OverflowText {...slotProps?.fileName} className={`${styles.fileName} ${slotProps?.fileName?.className || ""}`}>
					{fileName}
				</OverflowText>
			)}
			{children}
		</div>
	);
}

type CoreFilePreviewProps = {
	file: File | CoreFileItem;
	onImgClick?: () => void;
	renderOverlay?: () => React.ReactNode;
	/**Fallback when image can't be viewed */
	children?: React.ReactNode;
	slotProps?: {
		container?: Omit<BoxProps, "children">;
		image?: Omit<ComponentPropsWithoutRef<"img">, "children" | "src">;
		imageWrapper?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		fallbackContainer?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		fallbackIcon?: FileRadioIconProps;
		fileName?: Omit<TypographyProps, "children"> & { hide?: boolean };
	};
};

export default function CoreFilePreview({
	file,
	renderOverlay,
	children,
	onImgClick,
	slotProps,
}: CoreFilePreviewProps) {
	const [hasError, setHasError] = useState(false);
	const isImage = checkIfImage(file.name);

	const isNativeFile = file instanceof File;
	const nativeFile = isNativeFile ? file : file.nativeFile;

	const previewUrl = useImagePreview(nativeFile);
	const finalUrl = nativeFile ? previewUrl : (file as CoreFileItem).url;

	useEffect(() => {
		setHasError(false);
	}, [finalUrl]);

	return (
		<Box
			type={onImgClick ? "button" : undefined}
			{...slotProps?.container}
			component={slotProps?.container?.component ?? (onImgClick ? "button" : "div")}
			onClick={onImgClick}
			className={`${styles.imagePreviewContainer} ${onImgClick ? styles.imgContainerButton : ""} ${slotProps?.container?.className || ""}`}
		>
			{finalUrl && !hasError && isImage ? (
				<div
					{...slotProps?.imageWrapper}
					className={`${styles.imageWrapper} ${slotProps?.imageWrapper?.className || ""}`}
				>
					<img
						alt={i18nPackage.t("coreComponents.filePreview.alt", { fileName: file.name })}
						{...slotProps?.image}
						src={finalUrl}
						className={`${styles.imagePreview} ${slotProps?.image?.className || ""}`}
						onError={(e) => {
							setHasError(true);
							slotProps?.image?.onError?.(e);
						}}
					/>
					{renderOverlay && renderOverlay()}
				</div>
			) : (
				<>{children ?? <CoreFileFallback fileName={file.name} slotProps={slotProps} />}</>
			)}
		</Box>
	);
}
